import React, { useCallback } from 'react';
import {
  IncidentMedicalTransportation,
  useIncidentMedicalTransportations,
  useCreateIncidentMedicalTransportation,
  useEditIncidentMedicalTransportation,
} from 'components/IAP/hooks/incidentMedicalTransportationHooks';
import { MedicalTransportationBase } from 'types/iapBaseTypes';
import { useActualIncidentId } from 'components/IAP/hooks/commonHooks';
import { MedicalPlanTable, FieldDescriptor } from './MedicalPlanTable';

const FIELDS: FieldDescriptor[] = [
  {
    type: 'text',
    accessorKey: 'ambulance_service',
    header: 'Ambulance Service',
    placeholder: 'Ambulance Service *',
    sortable: true,
    maxLength: 200,
  },
  {
    type: 'text',
    accessorKey: 'address',
    header: 'Address',
    placeholder: 'Address',
  },
  {
    type: 'text',
    accessorKey: 'contact_number',
    header: 'Contact',
    placeholder: 'Contact',
    maxLength: 40,
  },
  {
    type: 'boolean',
    accessorKey: 'paramedics_on_board',
    header: 'Paramedics',
    ariaLabel: 'Paramedics on board',
  },
  {
    type: 'grouped-boolean',
    header: 'Level of Service',
    mode: 'radio',
    options: [
      { accessorKey: 'als', label: 'ALS', ariaLabel: 'Advanced Life Support' },
      { accessorKey: 'bls', label: 'BLS', ariaLabel: 'Basic Life Support' },
    ],
  },
];

const EMPTY_DRAFT = {
  ambulance_service: '',
  address: '',
  contact_number: '',
  paramedics_on_board: false,
  als: false,
  bls: false,
};

const toEditPayload = (
  entity: IncidentMedicalTransportation,
  override: Record<string, string | boolean>
): MedicalTransportationBase & {
  id: string;
  transportation_id: string;
  archived: boolean;
} => ({
  id: entity.id,
  transportation_id: entity.transportation_id,
  ambulance_service: entity.ambulance_service,
  description: entity.description,
  address: entity.address,
  contact_number: entity.contact_number,
  paramedics_on_board: entity.paramedics_on_board,
  als: entity.als,
  bls: entity.bls,
  archived: entity.archived,
  ...override,
});

type Props = { readOnly?: boolean };

export const TransportationsTable: React.FC<Props> = ({ readOnly = false }) => {
  const { data: rawEntities } = useIncidentMedicalTransportations('incident');
  const entities = (rawEntities ?? []).filter((e) => !e.archived);

  const incidentId = useActualIncidentId();
  const { mutate: createRecord, isPending: isCreating } =
    useCreateIncidentMedicalTransportation();
  const { mutate: editRecord } = useEditIncidentMedicalTransportation();

  const onSaveDraft = useCallback(
    (draft: Record<string, string | boolean>, onSuccess: () => void) => {
      createRecord(
        {
          incident_id: incidentId,
          ambulance_service: draft.ambulance_service as string,
          address: draft.address as string,
          contact_number: draft.contact_number as string,
          paramedics_on_board: draft.paramedics_on_board as boolean,
          als: draft.als as boolean,
          bls: draft.bls as boolean,
        },
        { onSuccess }
      );
    },
    [createRecord, incidentId]
  );

  const onSaveField = useCallback(
    (
      entity: IncidentMedicalTransportation,
      field: string,
      value: string | boolean
    ) => {
      editRecord(toEditPayload(entity, { [field]: value }));
    },
    [editRecord]
  );

  const onSaveFields = useCallback(
    (
      entity: IncidentMedicalTransportation,
      updates: Record<string, string | boolean>
    ) => {
      editRecord(toEditPayload(entity, updates));
    },
    [editRecord]
  );

  const onDelete = useCallback(
    (entity: IncidentMedicalTransportation) => {
      editRecord(toEditPayload(entity, { archived: true }));
    },
    [editRecord]
  );

  return (
    <MedicalPlanTable
      title="Transportation"
      addButtonLabel="Add Transport"
      noDataMessage="No transportation providers defined. Click Add Transport to get started."
      entities={entities}
      emptyDraft={EMPTY_DRAFT}
      requiredField="ambulance_service"
      fields={FIELDS}
      isCreating={isCreating}
      onSaveDraft={onSaveDraft}
      onSaveField={onSaveField}
      onSaveFields={onSaveFields}
      onDelete={onDelete}
      readOnly={readOnly}
    />
  );
};
